import type { AssessmentRubric } from '@/lib/api/rubric';

/**
 * Marks as the grader typed them, keyed by criterion id. Kept as strings
 * rather than numbers so a half-typed field ('' or '1.') stays exactly as
 * entered instead of collapsing to 0 or NaN under the caret.
 */
export type RubricMarks = Record<string, string>;

/** The marks that are complete enough to send, as numbers. */
export function rubricMarksToNumbers(marks: RubricMarks): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, raw] of Object.entries(marks)) {
    const n = Number(raw);
    if (raw.trim() !== '' && Number.isFinite(n)) out[id] = n;
  }
  return out;
}

export function rubricTotal(marks: RubricMarks): number {
  return Object.values(rubricMarksToNumbers(marks)).reduce((a, b) => a + b, 0);
}

/**
 * The ids whose mark is unusable — non-numeric, negative, or above the
 * criterion's ceiling. The server rejects all three; catching them here means
 * the grader is told which line is wrong rather than being handed one toast
 * about the whole form.
 */
export function rubricInvalidIds(rubric: AssessmentRubric, marks: RubricMarks): string[] {
  return rubric.criteria
    .filter((c) => {
      const raw = marks[c.id];
      if (raw === undefined || raw.trim() === '') return false; // blank scores 0
      const n = Number(raw);
      return !Number.isFinite(n) || n < 0 || n > c.max_marks;
    })
    .map((c) => c.id);
}

export function RubricGrader({
  rubric,
  maxScore,
  marks,
  onChange,
  disabled,
}: {
  rubric: AssessmentRubric;
  maxScore: number;
  marks: RubricMarks;
  onChange: (next: RubricMarks) => void;
  disabled?: boolean;
}) {
  const invalid = new Set(rubricInvalidIds(rubric, marks));
  const total = rubricTotal(marks);

  return (
    <div className="rounded-xl border border-[#ececec] bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-[#f3f4f6] px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#6b7280]">Rubric</span>
        <span className="text-xs text-[#9ca3af]">Blank counts as 0</span>
      </div>

      <ul className="divide-y divide-[#f3f4f6]">
        {rubric.criteria.map((c) => {
          const bad = invalid.has(c.id);
          return (
            <li
              key={c.id}
              // Stacked below sm so a 390px screen never puts a number input
              // and a long descriptor on the same line.
              className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
            >
              <div className="min-w-0">
                <label htmlFor={`rubric-${c.id}`} className="block text-sm font-medium text-[#111827]">
                  {c.label}
                </label>
                {c.descriptor && (
                  <p className="mt-0.5 text-xs leading-relaxed text-[#6b7280]">{c.descriptor}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <input
                  id={`rubric-${c.id}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={c.max_marks}
                  step="any"
                  disabled={disabled}
                  value={marks[c.id] ?? ''}
                  onChange={(e) => onChange({ ...marks, [c.id]: e.target.value })}
                  aria-invalid={bad}
                  aria-label={`Marks for ${c.label}, out of ${c.max_marks}`}
                  placeholder="0"
                  className={`w-20 rounded-lg border bg-white px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-[#ff5a1f] disabled:opacity-50 ${
                    bad ? 'border-[#dc2626]' : 'border-[#ececec]'
                  }`}
                />
                <span className="text-sm text-[#9ca3af]">/ {c.max_marks}</span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-2 border-t border-[#f3f4f6] bg-[#fafafa] px-3 py-2.5">
        <span className="text-sm font-semibold text-[#111827]">Total</span>
        <span className="text-sm font-semibold tabular-nums text-[#111827]">
          {total} <span className="font-normal text-[#9ca3af]">/ {maxScore}</span>
        </span>
      </div>

      {invalid.size > 0 && (
        <p role="alert" className="border-t border-[#f3f4f6] px-3 py-2 text-xs text-[#dc2626]">
          {invalid.size === 1 ? 'One criterion is' : `${invalid.size} criteria are`} out of range —
          each mark must be between 0 and that line&apos;s maximum.
        </p>
      )}
    </div>
  );
}
